import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import XeroReauthModal from './XeroReauthModal';
import { getXeroReauthTenants } from '../../utils/api_integrations';
import { getUserProfile } from '../../utils/api_dashboard';

// ---------------------------------------------------------------- #
// XeroReauthPrompt
// ---------------------------------------------------------------- #
//
// Xero re-authorisation, a one-off for Xero's move to granular scopes.
//
// Xero retires the broad Accounting API scopes on 13 September 2027. Tenants authorised before
// 1 October 2026 hold only the broad scopes, so their admins are asked to send each organisation
// through Xero's consent screen once. Mappings and settings are not touched.
//
// This file can be removed once every active tenant is re-authorised, that is when this returns 0:
//
//   SELECT COUNT(*) FROM ParkiXero.xero_integrations
//    WHERE isActive = '1' AND ( dateAuthorised IS NULL OR dateAuthorised < '2026-10-01' )
//      AND entityId NOT IN ( 11, 77 );
//
// To remove it: delete this folder and the lines marked "remove with xero-reauth" in the React app,
// then parki-api/integrations/XeroReauthorisation.php and every line in parki-api/integrations/APIResponse.php
// marked "remove with XeroReauthorisation.php".
//
// This component decides whether to show XeroReauthModal. It is mounted once in PrivateRoute and
// stays mounted while the user moves between private pages, so the check below runs once per page
// load and an open modal survives navigation. The steps are:
//   1. Coming back from Xero: read the result and take it out of the address bar.
//   2. Otherwise leave if the modal was already shown for this login.
//   3. Otherwise leave unless the user is an Entity Admin.
//   4. Load the organisations still to do.
//   5. Save this login as shown and open the modal.
// A result from Xero always opens the modal, whatever step 2 would have said.
// Nothing here ever blocks the user: every failure leaves the modal closed, without a message.

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The localStorage key holding the auth_time of the login the modal was last shown for. */
const SHOWN_FOR_KEY = 'xeroReauthPromptShownFor';

/** The address bar parameters the API sends back from Xero. */
const RESULT_PARAMETERS = ['xeroReauth', 'reason', 'tenant', 'ticked'];

/** The HTTP status for an expired session. */
const HTTP_UNAUTHORISED = 401;

/** The HTTP status for a user who is not an Entity Admin. */
const HTTP_FORBIDDEN = 403;

// ---------------------------------------------------------------- #
// Reading the browser: address bar, login and localStorage
// ---------------------------------------------------------------- #

/**
 * Treat true, 1 and "1" as yes, because the profile API may send the flag as any of them.
 */
const isYesFlag = (value) => {
  return value === true || value === 1 || value === '1';
};

/**
 * Read a localStorage value, or null when storage is blocked.
 */
const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
};

/**
 * Save a localStorage value. When storage is blocked the modal shows again on the next page load.
 */
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    // Nothing else to do: the modal is only a reminder
  }
};

/**
 * The result the API put in the address bar after Xero, or null when there is none.
 */
const readResultFromSearch = (search) => {
  // No xeroReauth parameter means this page load did not come from Xero
  const params = new URLSearchParams(search);
  const status = params.get('xeroReauth');
  if (!status) {
    return null;
  }

  return {
    status: status,
    reason: params.get('reason'),
    tenant: params.get('tenant'),
    ticked: params.get('ticked')
  };
};

/**
 * Read auth_time from the stored Cognito access token. Null when there is no token, or it is
 * opaque or has no such claim.
 */
const readAuthTime = () => {
  // A JWT is three dot-separated parts; the middle one holds the claims
  const token = readStorage('authToken');
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  // Turn base64url into plain base64, padded to a multiple of 4 characters
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }

  // Decode the claims; there is nothing to verify in the browser
  let claims = null;
  try {
    claims = JSON.parse(atob(base64));
  } catch (err) {
    return null;
  }

  // Claims that are not an object, or have no auth_time, tell us nothing
  const hasAuthTime = claims && claims.auth_time !== undefined && claims.auth_time !== null;
  if (!hasAuthTime) {
    return null;
  }
  return String(claims.auth_time);
};

/**
 * The isEntityAdmin flag saved in localStorage, or undefined when it has not been saved.
 * Login stores only the user's name, so the flag appears once a page has loaded the profile.
 * auth.getCurrentUser leaves this field out, so the stored user is read directly.
 */
const readStoredAdminFlag = () => {
  // Nothing stored yet
  const storedUser = readStorage('user');
  if (!storedUser) {
    return undefined;
  }

  // A damaged value counts as not known
  try {
    const user = JSON.parse(storedUser);
    if (!user) {
      return undefined;
    }
    return user.isEntityAdmin;
  } catch (err) {
    return undefined;
  }
};

/**
 * Work out whether the user is an Entity Admin, loading the profile when localStorage does not say.
 * This costs one extra profile call on the first page after a login, and keeps non-admins from
 * sending the tenants request.
 */
const loadIsEntityAdmin = async () => {
  // Use the stored flag when a page has saved it
  const storedFlag = readStoredAdminFlag();
  if (storedFlag !== undefined) {
    return isYesFlag(storedFlag);
  }

  // Otherwise ask the API, the same call the sidebar's pages make
  const profileData = await getUserProfile();
  return isYesFlag(profileData?.userData?.isEntityAdmin);
};

/**
 * The HTTP status of a failed call. The integrations client and plain axios keep it in different places.
 */
const getErrorStatus = (error) => {
  // The integrations client copies the status onto the API's envelope
  if (error && error.httpStatus) {
    return error.httpStatus;
  }

  // A plain axios error, or no status at all
  return error?.response?.status;
};

// ---------------------------------------------------------------- #
// The prompt
// ---------------------------------------------------------------- #

/**
 * Decide once per page load whether to show the Xero re-authorisation modal, and show it.
 */
const XeroReauthPrompt = () => {
  const history = useHistory();
  const location = useLocation();

  // The modal's contents, and whether it is open
  const [isOpen, setIsOpen] = useState(false);
  const [tenants, setTenants] = useState(null);
  const [canReauthorise, setCanReauthorise] = useState(false);
  const [xeroResult, setXeroResult] = useState(null);

  // Runs once per page load: navigating between private pages keeps this component mounted
  useEffect(() => {
    let cancelled = false;

    /**
     * Take the Xero result out of the address bar, keeping every other parameter.
     */
    const stripResultFromAddressBar = () => {
      // Remove only our parameters
      const params = new URLSearchParams(location.search);
      for (const name of RESULT_PARAMETERS) {
        params.delete(name);
      }

      // Put back whatever is left, if anything
      const remainingSearch = params.toString();
      let newSearch = '';
      if (remainingSearch) {
        newSearch = `?${remainingSearch}`;
      }

      // Replace the history entry, so Back does not show the result again
      history.replace({
        pathname: location.pathname,
        search: newSearch,
        hash: location.hash,
        state: location.state
      });
    };

    /**
     * Work through the steps in the file header, then open the modal if there is anything to show.
     */
    const decideWhetherToShow = async () => {
      // Step 1: a result from Xero is read and removed before anything else
      const resultFromXero = readResultFromSearch(location.search);
      const cameFromXero = resultFromXero !== null;
      if (cameFromXero) {
        stripResultFromAddressBar();
      }

      // Step 2: without a result, show the modal once per login
      const authTime = readAuthTime();
      if (!cameFromXero) {
        if (authTime === null) {
          return;
        }
        const shownForAuthTime = readStorage(SHOWN_FOR_KEY);
        if (shownForAuthTime === authTime) {
          return;
        }
      }

      // Step 3: without a result, only Entity Admins go on; a result skips this so it never waits on the profile
      if (!cameFromXero) {
        let isEntityAdmin = false;
        try {
          isEntityAdmin = await loadIsEntityAdmin();
        } catch (err) {
          return;
        }
        if (cancelled || !isEntityAdmin) {
          return;
        }
      }

      // Step 4: load the list
      let loadedTenants = null;
      let loadedCanReauthorise = false;
      try {
        const response = await getXeroReauthTenants();
        const data = response.data || {};
        loadedTenants = data.tenants || [];
        loadedCanReauthorise = data.canReauthorise === true;
      } catch (err) {
        // Without a result to show, a failed list just leaves the modal closed
        if (!cameFromXero) {
          return;
        }

        // An expired session or a non-admin stays quiet, even with a result in the address bar
        const errorStatus = getErrorStatus(err);
        const isExpiredSession = errorStatus === HTTP_UNAUTHORISED;
        const isNotAdmin = errorStatus === HTTP_FORBIDDEN;
        if (isExpiredSession || isNotAdmin) {
          return;
        }

        // Anything else: still show the result, with no list under it
        loadedTenants = null;
      }

      // The page changed underneath us, for example a logout
      if (cancelled) {
        return;
      }

      // Nothing to do and nothing to report
      const hasNoTenants = !loadedTenants || loadedTenants.length === 0;
      if (hasNoTenants && !cameFromXero) {
        return;
      }

      // Step 5: remember this login, then open the modal
      if (authTime !== null) {
        writeStorage(SHOWN_FOR_KEY, authTime);
      }
      setTenants(loadedTenants);
      setCanReauthorise(loadedCanReauthorise);
      setXeroResult(resultFromXero);
      setIsOpen(true);
    };

    decideWhetherToShow();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Close the modal. It comes back with the remaining organisations on the next login.
   */
  const handleClose = () => {
    setIsOpen(false);
  };

  // Nothing to show
  if (!isOpen) {
    return null;
  }

  return (
    <XeroReauthModal
      tenants={tenants}
      canReauthorise={canReauthorise}
      result={xeroResult}
      onClose={handleClose}
    />
  );
};

export default XeroReauthPrompt;
